/**
 * Static collision. Owned by `world`.
 *
 * A uniform broadphase grid over axis-aligned boxes (buildings, parked cars, garages, fences)
 * and circles (poles, trees, hydrants). No per-triangle work anywhere — the vehicle asks
 * "what am I overlapping" a handful of times per fixed step and gets pooled contacts back
 * with zero allocation.
 *
 * Kerbs are deliberately NOT colliders. sampleGround() reports the raised sidewalk height and
 * the truck drives up it, which is the arcade behaviour we want.
 */

import * as THREE from 'three';

const KIND_BUILDING = 0, KIND_CAR = 1, KIND_POLE = 2, KIND_TREE = 3, KIND_FENCE = 4, KIND_PROP = 5;
const KIND_NAMES = ['building', 'car', 'pole', 'tree', 'fence', 'prop'];

export class StaticCollision {
  constructor(bounds, cell, maxContacts) {
    this.bounds = bounds;
    this.cell = cell;
    this.nx = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) / cell) + 1);
    this.nz = Math.max(1, Math.ceil((bounds.maxZ - bounds.minZ) / cell) + 1);

    this._boxes = [];    // {minX,minZ,maxX,maxZ,y0,y1,kind}
    this._circles = [];  // {x,z,r,y0,y1,kind}

    this.contacts = new Array(maxContacts);
    for (let i = 0; i < maxContacts; i++) {
      this.contacts[i] = { normal: new THREE.Vector3(), depth: 0, kind: 'building', point: new THREE.Vector3() };
    }
    this._result = [];
    this._ray = { hit: false, point: new THREE.Vector3(), normal: new THREE.Vector3(), kind: 'building', distance: 0 };
  }

  addBox(minX, minZ, maxX, maxZ, y0, y1, kindName) {
    this._boxes.push({ minX, minZ, maxX, maxZ, y0, y1, kind: KIND_NAMES.indexOf(kindName) });
  }

  addCircle(x, z, r, y0, y1, kindName) {
    this._circles.push({ x, z, r, y0, y1, kind: KIND_NAMES.indexOf(kindName) });
  }

  /** Rotated box → conservative AABB. Parked cars and garages are near axis-aligned anyway. */
  addOriented(x, z, halfL, halfW, yaw, y0, y1, kindName) {
    const c = Math.abs(Math.cos(yaw)), s = Math.abs(Math.sin(yaw));
    const ex = halfW * c + halfL * s;
    const ez = halfL * c + halfW * s;
    this.addBox(x - ex, z - ez, x + ex, z + ez, y0, y1, kindName);
  }

  /** CSR broadphase. Call once, after every collider is in. */
  bake() {
    const total = this._boxes.length + this._circles.length;
    const counts = new Uint32Array(this.nx * this.nz);
    const spans = new Array(total);
    for (let i = 0; i < total; i++) {
      const s = this._span(i);
      spans[i] = s;
      for (let gz = s.z0; gz <= s.z1; gz++) {
        for (let gx = s.x0; gx <= s.x1; gx++) counts[gz * this.nx + gx]++;
      }
    }
    this.start = new Uint32Array(this.nx * this.nz + 1);
    let acc = 0;
    for (let i = 0; i < counts.length; i++) { this.start[i] = acc; acc += counts[i]; }
    this.start[counts.length] = acc;
    this.items = new Int32Array(acc);
    const cursor = this.start.slice(0, counts.length);
    for (let i = 0; i < total; i++) {
      const s = spans[i];
      for (let gz = s.z0; gz <= s.z1; gz++) {
        for (let gx = s.x0; gx <= s.x1; gx++) this.items[cursor[gz * this.nx + gx]++] = i;
      }
    }
    this.boxCount = this._boxes.length;
    this.total = total;
    return this;
  }

  _span(i) {
    let minX, minZ, maxX, maxZ;
    if (i < this._boxes.length) {
      const b = this._boxes[i];
      minX = b.minX; minZ = b.minZ; maxX = b.maxX; maxZ = b.maxZ;
    } else {
      const c = this._circles[i - this._boxes.length];
      minX = c.x - c.r; minZ = c.z - c.r; maxX = c.x + c.r; maxZ = c.z + c.r;
    }
    return {
      x0: this._gx(minX), x1: this._gx(maxX),
      z0: this._gz(minZ), z1: this._gz(maxZ),
    };
  }

  _gx(x) { return Math.max(0, Math.min(this.nx - 1, Math.floor((x - this.bounds.minX) / this.cell))); }
  _gz(z) { return Math.max(0, Math.min(this.nz - 1, Math.floor((z - this.bounds.minZ) / this.cell))); }

  /**
   * Sphere vs. world. Accepts a THREE.Sphere, {center,radius} or {x,y,z,r}.
   * Returns a REUSED array of pooled contacts — valid until the next call.
   */
  collide(sphere) {
    const res = this._result;
    res.length = 0;
    if (!sphere) return res;
    let px, py, pz, r;
    if (sphere.center) { px = sphere.center.x; py = sphere.center.y; pz = sphere.center.z; r = sphere.radius; }
    else { px = sphere.x; py = sphere.y || 0; pz = sphere.z; r = sphere.r != null ? sphere.r : sphere.radius; }
    if (!(r > 0)) return res;

    const gx0 = this._gx(px - r), gx1 = this._gx(px + r);
    const gz0 = this._gz(pz - r), gz1 = this._gz(pz + r);
    const seen = this._seen || (this._seen = new Set());
    seen.clear();
    let n = 0;

    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const cellIdx = gz * this.nx + gx;
        const s = this.start[cellIdx], e = this.start[cellIdx + 1];
        for (let k = s; k < e; k++) {
          const id = this.items[k];
          if (seen.has(id)) continue;
          seen.add(id);
          if (n >= this.contacts.length) return res;
          const c = this.contacts[n];
          if (id < this.boxCount) {
            if (this._hitBox(this._boxes[id], px, py, pz, r, c)) { res.push(c); n++; }
          } else if (this._hitCircle(this._circles[id - this.boxCount], px, py, pz, r, c)) { res.push(c); n++; }
        }
      }
    }
    return res;
  }

  _hitBox(b, px, py, pz, r, out) {
    if (py > b.y1 + r || py < b.y0 - r) return false;
    const cx = px < b.minX ? b.minX : (px > b.maxX ? b.maxX : px);
    const cz = pz < b.minZ ? b.minZ : (pz > b.maxZ ? b.maxZ : pz);
    const dx = px - cx, dz = pz - cz;
    const d2 = dx * dx + dz * dz;
    if (d2 > r * r) return false;
    let nx, nz, depth;
    if (d2 > 1e-8) {
      const d = Math.sqrt(d2);
      nx = dx / d; nz = dz / d; depth = r - d;
    } else {
      // centre is inside — push out along the shallowest axis
      const dl = px - b.minX, dr = b.maxX - px, db = pz - b.minZ, dt = b.maxZ - pz;
      const m = Math.min(dl, dr, db, dt);
      if (m === dl) { nx = -1; nz = 0; } else if (m === dr) { nx = 1; nz = 0; }
      else if (m === db) { nx = 0; nz = -1; } else { nx = 0; nz = 1; }
      depth = r + m;
    }
    out.normal.set(nx, 0, nz);
    out.depth = depth;
    out.kind = KIND_NAMES[b.kind] || 'building';
    out.point.set(cx, py, cz);
    return true;
  }

  _hitCircle(c, px, py, pz, r, out) {
    if (py > c.y1 + r || py < c.y0 - r) return false;
    const dx = px - c.x, dz = pz - c.z;
    const d2 = dx * dx + dz * dz;
    const rr = r + c.r;
    if (d2 > rr * rr) return false;
    const d = Math.sqrt(d2) || 1e-4;
    out.normal.set(dx / d, 0, dz / d);
    out.depth = rr - d;
    out.kind = KIND_NAMES[c.kind] || 'prop';
    out.point.set(c.x + (dx / d) * c.r, py, c.z + (dz / d) * c.r);
    return true;
  }

  /**
   * Ray vs. world. 2D DDA over the broadphase grid; slab test per candidate.
   * Returns a REUSED record or null.
   */
  raycast(ox, oy, oz, dx, dy, dz, maxDist) {
    const inv = 1 / (Math.sqrt(dx * dx + dy * dy + dz * dz) || 1);
    dx *= inv; dy *= inv; dz *= inv;
    let best = maxDist, bestId = -1;

    let gx = this._gx(ox), gz = this._gz(oz);
    const stepX = dx > 0 ? 1 : -1, stepZ = dz > 0 ? 1 : -1;
    const cellW = this.cell;
    const bx = this.bounds.minX + gx * cellW, bz = this.bounds.minZ + gz * cellW;
    let tMaxX = Math.abs(dx) < 1e-9 ? Infinity : ((dx > 0 ? bx + cellW - ox : ox - bx) / Math.abs(dx));
    let tMaxZ = Math.abs(dz) < 1e-9 ? Infinity : ((dz > 0 ? bz + cellW - oz : oz - bz) / Math.abs(dz));
    const tDeltaX = Math.abs(dx) < 1e-9 ? Infinity : cellW / Math.abs(dx);
    const tDeltaZ = Math.abs(dz) < 1e-9 ? Infinity : cellW / Math.abs(dz);

    let guard = 0;
    let t = 0;
    while (t <= maxDist && guard++ < 512) {
      if (gx >= 0 && gx < this.nx && gz >= 0 && gz < this.nz) {
        const cellIdx = gz * this.nx + gx;
        for (let k = this.start[cellIdx]; k < this.start[cellIdx + 1]; k++) {
          const id = this.items[k];
          const hit = id < this.boxCount
            ? rayBox(this._boxes[id], ox, oy, oz, dx, dy, dz)
            : rayCircle(this._circles[id - this.boxCount], ox, oy, oz, dx, dy, dz);
          if (hit >= 0 && hit < best) { best = hit; bestId = id; }
        }
      }
      if (bestId >= 0 && best < t) break;
      if (tMaxX < tMaxZ) { t = tMaxX; tMaxX += tDeltaX; gx += stepX; }
      else { t = tMaxZ; tMaxZ += tDeltaZ; gz += stepZ; }
      if (gx < 0 || gx >= this.nx || gz < 0 || gz >= this.nz) break;
    }
    if (bestId < 0) return null;

    const out = this._ray;
    out.hit = true;
    out.distance = best;
    out.point.set(ox + dx * best, oy + dy * best, oz + dz * best);
    if (bestId < this.boxCount) {
      const b = this._boxes[bestId];
      const ex = 1e-3;
      const p = out.point;
      if (Math.abs(p.x - b.minX) < ex) out.normal.set(-1, 0, 0);
      else if (Math.abs(p.x - b.maxX) < ex) out.normal.set(1, 0, 0);
      else if (Math.abs(p.z - b.minZ) < ex) out.normal.set(0, 0, -1);
      else if (Math.abs(p.z - b.maxZ) < ex) out.normal.set(0, 0, 1);
      else out.normal.set(0, 1, 0);
      out.kind = KIND_NAMES[b.kind] || 'building';
    } else {
      const c = this._circles[bestId - this.boxCount];
      out.normal.set(out.point.x - c.x, 0, out.point.z - c.z).normalize();
      out.kind = KIND_NAMES[c.kind] || 'prop';
    }
    return out;
  }

  stats() { return { boxes: this._boxes.length, circles: this._circles.length, cells: this.nx * this.nz }; }
}

function rayBox(b, ox, oy, oz, dx, dy, dz) {
  let t0 = 0, t1 = Infinity;
  const slab = (o, d, lo, hi) => {
    if (Math.abs(d) < 1e-9) return o >= lo && o <= hi;
    let a = (lo - o) / d, bb = (hi - o) / d;
    if (a > bb) { const t = a; a = bb; bb = t; }
    if (a > t0) t0 = a;
    if (bb < t1) t1 = bb;
    return t1 >= t0;
  };
  if (!slab(ox, dx, b.minX, b.maxX)) return -1;
  if (!slab(oz, dz, b.minZ, b.maxZ)) return -1;
  if (!slab(oy, dy, b.y0, b.y1)) return -1;
  return t0 >= 0 ? t0 : -1;
}

function rayCircle(c, ox, oy, oz, dx, dy, dz) {
  const ex = ox - c.x, ez = oz - c.z;
  const a = dx * dx + dz * dz;
  if (a < 1e-9) return -1;
  const bq = 2 * (ex * dx + ez * dz);
  const cq = ex * ex + ez * ez - c.r * c.r;
  const disc = bq * bq - 4 * a * cq;
  if (disc < 0) return -1;
  const t = (-bq - Math.sqrt(disc)) / (2 * a);
  if (t < 0) return -1;
  const y = oy + dy * t;
  if (y < c.y0 || y > c.y1) return -1;
  return t;
}

export { KIND_NAMES, KIND_BUILDING, KIND_CAR, KIND_POLE, KIND_TREE, KIND_FENCE, KIND_PROP };
